import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import TextField from "@/components/ui/Textfield";
import TextareaField from "@/components/ui/Textareafield";
import SearchableDropdown from "@/components/ui/SearchDropDown";
import Button from "@/components/ui/Button";
import Spinner from "@/components/ui/Spinner";
import { BiPlus } from "react-icons/bi";
import { apiClient } from "@/api/client";
import { AxiosError } from "axios";
import useGlobalStore from "@/store/globalStore";
import { useNavigate, useLocation } from "react-router-dom";
import useObjectiveStore from "./store/objectiveStore";
import useAuthStore from "@/store/authStore";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function CreateObjectives() {
  const { formData, setFormData, addObjective, editObjective } =
    useObjectiveStore();

  const [mediaAgencyOptions, setMediaAgencyOptions] = useState([]);
  const { showSuccess, showError } = useGlobalStore();

  const { user_role } = useAuthStore();

  const navigate = useNavigate();
  const query = useQuery();
  const objectiveId = query.get("id");
  const isEditing = !!objectiveId;

  const fetchMediaAgencies = async () => {
    try {
      const response = await apiClient.get(`/media-agencies`);
      setMediaAgencyOptions(
        response.data.map((e: any) => ({
          title: e.name,
          value: e.id,
        }))
      );
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.error || "Failed to fetch media agencies.";
      showError(errorMessage);
    }
  };

  useEffect(() => {
    fetchMediaAgencies();
  }, [objectiveId]);

  const handleSubmit = async () => {
    try {
      if (isEditing && objectiveId) {
        await editObjective(objectiveId);
        showSuccess("Objective updated successfully");
      } else {
        await addObjective();
        showSuccess("Objective added successfully");
      }
      navigate("/objectives");
    } catch (error: any) {
      showError(error?.message ?? "Something went wrong");
    }
  };

  return (
    <Layout>
      <div>
        <div className="font-semibold text-3xl">
          {isEditing ? "Edit Objective" : "Add New Objective"}
        </div>
        <hr className="border-t border-gray-300 mt-3" />
        <div className="mt-8">
          <div className="grid grid-cols-12">
            <div className="font-semibold text-sm mb-1 col-span-2">
              Objective Name:
            </div>
            <TextField
              type="text"
              placeholder="English"
              onChange={(e) => setFormData({ title: e.target.value })}
              value={formData.title}
              className="col-span-8 w-1/2"
            />
          </div>

          <div className="mt-3 grid grid-cols-12">
            <div className="font-semibold text-sm mb-1 col-span-2">
              Description:
            </div>
            <TextareaField
              onChange={(e) => setFormData({ description: e.target.value })}
              value={formData.description}
              className="col-span-8 w-1/2"
            />
          </div>

          {user_role === "Global Admin" && (
            <div className="mt-3 grid grid-cols-12">
              <div className="font-semibold text-sm mb-1 col-span-2">
                Media Agency:
              </div>
              <SearchableDropdown
                selected={formData.media_agency_id}
                setSelected={(value) =>
                  setFormData({ media_agency_id: value.toString() })
                }
                options={mediaAgencyOptions}
                className="col-span-8 w-1/2"
              />
            </div>
          )}

          <div className="flex justify-center">
            {formData.isLoading ? (
              <Spinner className="mt-10" size={30} />
            ) : (
              <Button
                type="primary"
                trailingIcon={<BiPlus />}
                className="mt-10"
                onClick={handleSubmit}
              >
                {isEditing ? "Update Objective" : "Add New Objective"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
